import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InputField } from 'src/components/atoms/input-field';
import { Dialog, DialogActions } from 'src/components/molecules/dialog';
import { useInviteEmployee } from 'src/features/employees/api/hooks/use-invite-employee';
import { usePrompt } from 'src/hooks/use-prompt';
import { z } from 'zod';

interface FormValues {
  email: string;
}

const defaultValues: FormValues = {
  email: '',
};

export const InviteEmployeeDialog = ({ open, onCancel }: DialogActions) => {
  const { t } = useTranslation();

  const handleOnCancel = () => {
    onCancel?.();
    reset(defaultValues);
  };

  const { mutate: inviteEmployee, isPending: inviteEmployeeIsLoading } =
    useInviteEmployee(handleOnCancel);

  const validationSchema = z.object({
    email: z
      .string()
      .email(t('validation:email'))
      .min(1, t('validation:required')),
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty },
  } = useForm<FormValues>({
    defaultValues,
    mode: 'onBlur',
    resolver: zodResolver(validationSchema),
  });

  usePrompt(isDirty);

  const handleInviteEmployee = ({ email }: FormValues) => inviteEmployee(email);

  return (
    <Dialog
      open={open}
      title={t('employees:employees.inviteEmployeeDialog.title')}
      onCancel={handleOnCancel}
      onConfirm={handleSubmit(handleInviteEmployee)}
      isConfirmButtonSubmitting={inviteEmployeeIsLoading}
      confirmButtonId="invite_employee_confirm_button"
      confirmButtonText={t('common:button.invite')}>
      <Controller
        control={control}
        name="email"
        render={({
          field: { onChange, onBlur, value },
          fieldState: { error, invalid },
        }) => (
          <InputField
            fullWidth
            type="email"
            sx={{ my: 1 }}
            label="Email"
            value={value}
            onBlur={onBlur}
            onChange={onChange}
            required
            id="invite_employee_email_input"
            error={invalid}
            helperText={error?.message}
          />
        )}
      />
    </Dialog>
  );
};
